/**
 * Schema-driven message hooks — single source of truth.
 *
 * Replaces both useGetAllMessages and useGetAllMessagesTypes.
 * Sources ALL data from the auto-generated FlatBuffer TypeScript enums.
 * No backend /messages/ or /message-type/ API calls are made.
 *
 * Messages are device-type-agnostic — the enum values are shared across
 * elevator, escalator, and all other device types. The device wrapper is
 * applied at auto-expand time when the bank assignment is known.
 */
import { useMemo } from "react";
import { MessageInterface } from "../interfaces/Message.interface";
import { MessageTypeInterface } from "../interfaces/MessageType.interface";

import { Status } from "../flatbuffers/generated/status/status";
import { Traffic } from "../flatbuffers/generated/traffic/traffic";
import { Usage } from "../flatbuffers/generated/usage/usage";
import { Service } from "../flatbuffers/generated/service/service";
import { Control } from "../flatbuffers/generated/control/control";
import { Fault } from "../flatbuffers/generated/fault/fault";
import { GroupSupervisoryStatus } from "../flatbuffers/generated/group-supervisory-status/group-supervisory-status";
import { GroupControl } from "../flatbuffers/generated/group-control/group-control";
import { ControllerInfoContent } from "../flatbuffers/generated/controller-info/controller-info-content";

// =====================================================================
// Message type IDs — stable, matching historical backend convention
// =====================================================================

export const MSG_TYPE_STATUS = 1;
export const MSG_TYPE_TRAFFIC = 2;
export const MSG_TYPE_USAGE = 3;
export const MSG_TYPE_SERVICE = 4;
export const MSG_TYPE_CONTROL = 5;
export const MSG_TYPE_FAULT = 6;
export const MSG_TYPE_GROUP_SUPERVISORY = 7;
export const MSG_TYPE_GROUP_CONTROL = 8;
export const MSG_TYPE_CONTROLLER_INFO = 9;

// =====================================================================
// Category definitions
// =====================================================================

interface CategoryDef {
  typeId: number;
  typeName: string;
  color: string;
  enm: Record<string, string | number>;
  /** 1 = OneHot, 2 = BitMask */
  contentType: 1 | 2;
}

const CATEGORIES: CategoryDef[] = [
  {
    typeId: MSG_TYPE_STATUS,
    typeName: "STATUS",
    color: "#1890ff",
    enm: Status,
    contentType: 2,
  },
  {
    typeId: MSG_TYPE_TRAFFIC,
    typeName: "TRAFFIC",
    color: "#52c41a",
    enm: Traffic,
    contentType: 2,
  },
  {
    typeId: MSG_TYPE_USAGE,
    typeName: "USAGE",
    color: "#faad14",
    enm: Usage,
    contentType: 1,
  },
  {
    typeId: MSG_TYPE_SERVICE,
    typeName: "SERVICE",
    color: "#722ed1",
    enm: Service,
    contentType: 1,
  },
  {
    typeId: MSG_TYPE_CONTROL,
    typeName: "CONTROL",
    color: "#13c2c2",
    enm: Control,
    contentType: 2,
  },
  {
    typeId: MSG_TYPE_FAULT,
    typeName: "FAULT",
    color: "#f5222d",
    enm: Fault,
    contentType: 2,
  },
  {
    typeId: MSG_TYPE_GROUP_SUPERVISORY,
    typeName: "GROUP_SUPERVISORY_STATUS",
    color: "#eb2f96",
    enm: GroupSupervisoryStatus,
    contentType: 2,
  },
  {
    typeId: MSG_TYPE_GROUP_CONTROL,
    typeName: "GROUP_CONTROL",
    color: "#fa8c16",
    enm: GroupControl,
    contentType: 2,
  },
  {
    typeId: MSG_TYPE_CONTROLLER_INFO,
    typeName: "CONTROLLER_INFO",
    color: "#2f54eb",
    enm: ControllerInfoContent,
    contentType: 2,
  },
];

// =====================================================================
// Helpers
// =====================================================================

function humanize(name: string): string {
  return name.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ");
}

function getEnumEntries(enm: Record<string, string | number>): [string, number][] {
  return Object.entries(enm)
    .filter(([key, value]) => {
      if (typeof value !== "number") return false;
      if (key === "NONE" && value === 0) return false;
      return true;
    })
    .map(([key, value]) => [key, value as number]);
}

// =====================================================================
// Pre-computed data (built once at module load)
// =====================================================================

function buildAllMessages(): MessageInterface[] {
  const now = new Date().toISOString();

  return CATEGORIES.flatMap((cat) => {
    const entries = getEnumEntries(cat.enm);
    return entries.map(([name, enumValue]) => ({
      id: `${cat.typeName}:${enumValue}`,
      shortMessage: humanize(name),
      description: `${cat.typeName}::${humanize(name)}`,
      enumId: enumValue,
      messageTypeId: cat.typeId,
      message_type: {
        id: cat.typeId,
        name: cat.typeName,
        created_at: now,
        updated_at: now,
        deleted_at: null,
      },
      messageContentType: cat.contentType as 1 | 2,
      deviceType: "ALL" as const,
      position: cat.typeName === "TRAFFIC",
      keys: [],
      custom_keys: [] as [],
      controller_info_msg_data: [] as [],
      escalator_controller_info_message_type: null,
      elevator_controller_info_message_type: null,
      escalator_status_message_type: null,
      elevator_status_message_type: null,
      debounce_time: null,
      created_at: now,
      updated_at: now,
      deleted_at: null,
    }));
  });
}

function buildAllMessageTypes(): MessageTypeInterface[] {
  return CATEGORIES.map((cat) => ({
    id: cat.typeId,
    name: cat.typeName,
    color: cat.color,
    messages: null,
  }));
}

/** All messages derived from FlatBuffer enums, computed once. */
export const ALL_SCHEMA_MESSAGES: MessageInterface[] = buildAllMessages();

/** All message type categories, computed once. */
export const ALL_SCHEMA_MESSAGE_TYPES: MessageTypeInterface[] = buildAllMessageTypes();

// =====================================================================
// Hooks (drop-in compatible with the old API-based hooks)
// =====================================================================

/**
 * Drop-in replacement for the old useGetAllMessages hook.
 * Same return shape, same filter parameters. No network calls.
 */
export function useSchemaMessages(
  opts: {
    page?: number;
    limit?: number;
    messageTypeId?: string;
    deviceType?: string;
  } = {},
) {
  const { messageTypeId = "" } = opts;

  const messages = useMemo(() => {
    let filtered = ALL_SCHEMA_MESSAGES;

    if (messageTypeId && messageTypeId !== "" && messageTypeId !== "99999") {
      const numId = parseInt(messageTypeId, 10);
      if (!Number.isNaN(numId)) {
        filtered = filtered.filter((m) => m.messageTypeId === numId);
      }
    }
    return filtered;
  }, [messageTypeId]);

  return {
    messages,
    isLoadingGetAllMessages: false,
    errorMessageGetAllMessages: "",
    isErrorGetAllMessages: false,
    totalRowsGetAllMessages: messages.length,
  };
}

/**
 * Drop-in replacement for the old useGetAllMessagesTypes hook.
 * Same return shape. No network calls.
 */
export function useSchemaMessageTypes() {
  return {
    messagesTypes: ALL_SCHEMA_MESSAGE_TYPES,
    isLoadingGetAllMessagesTypes: false,
    errorMessageGetAllMessagesTypes: "",
    isErrorGetAllMessagesTypes: false,
    totalRowsGetAllMessagesTypes: ALL_SCHEMA_MESSAGE_TYPES.length,
  };
}
